import {
  ChangeDetectorRef,
  Component,
  ViewEncapsulation,
  forwardRef,
  inject,
  input,
  linkedSignal,
  signal,
  computed
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-checkbox',
    templateUrl: './checkbox.component.html',
    styleUrls: ['./checkbox.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CheckboxComponent),
            multi: true
        }
    ],
    imports: [FormsModule]
})
export class CheckboxComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);

  label = input<string>('');
  disabled = input(false);
  value = input<boolean>(false);
  variant = input<'default' | 'error' | 'boundarycondition'>('default');

  /** Internal disabled state that can be toggled by CVA setDisabledState */
  _disabledByCva = signal(false);

  /** Effective disabled state */
  isDisabled = computed(() => this.disabled() || this._disabledByCva());

  /**
   * Writable signal linked to the `value` input.
   * Follows parent changes but can be independently set by writeValue() (CVA).
   */
  private _value = linkedSignal<boolean, boolean>({
    source: this.value,
    computation: (src) => src || false
  });
  private onChange = (value: boolean) => {};
  private onTouched = () => {};

  get internalValue(): boolean {
    return this._value();
  }

  set internalValue(val: boolean) {
    if (val !== this._value()) {
      this._value.set(val);
      this.onChange(val);
      this.cdr.detectChanges();
    }
  }

  writeValue(value: boolean): void {
    this._value.set(value || false);
    this.cdr.detectChanges();
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled);
    this.cdr.detectChanges();
  }

  onCheckboxChange(e: Event): void {
    const target = e.target as HTMLInputElement;
    this.internalValue = target.checked;
  }

  onBlur(): void {
    this.onTouched();
  }

  getSanitizedLabel(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.label());
  }
}
